import { sha256 } from '@/utils/sha256'
import { StorageKeys, StorageService } from '@/utils/storageService'

/**
 * PKCE (RFC 7636) and state (RFC 6749 §10.12) helpers for the eHealth OAuth2
 * login flow.
 *
 * The verifier is a high-entropy secret that never leaves the app until the
 * token exchange; only its S256 challenge travels with the authorize request.
 * The state is echoed back by the server on the callback and must match the
 * one this app generated, so a callback can't be injected into a login the
 * app never started.
 */

// Matches the server's authorization_request expiry — an attempt older than
// this belongs to a login that can no longer complete
const ATTEMPT_TTL_MS = 10 * 60 * 1000

type StoredLoginAttempt = {
  verifier: string
  state: string
  createdAt: number
}

type LoginAttempt = Pick<StoredLoginAttempt, 'verifier' | 'state'>

/** What the authorize request carries: the S256 challenge and the state. */
export type LoginAttemptStart = {
  codeChallenge: string
  state: string
}

const base64UrlEncode = (bytes: Uint8Array): string =>
  btoa(String.fromCodePoint(...bytes))
    .replaceAll('+', '-')
    .replaceAll('/', '_')
    .replace(/=+$/, '')

/**
 * Generate a code verifier: 32 random bytes, base64url-encoded (43 chars,
 * within the 43–128 range required by RFC 7636 §4.1). Deliberately not built
 * on @bothrs/util/random — that module is Math.random()-based and a PKCE
 * verifier needs a cryptographic source.
 */
export const generateCodeVerifier = (): string => {
  const bytes = new Uint8Array(32)
  crypto.getRandomValues(bytes)
  return base64UrlEncode(bytes)
}

/**
 * Compute the S256 code challenge: BASE64URL(SHA256(verifier)) per RFC 7636 §4.2.
 */
export const computeCodeChallenge = async (codeVerifier: string): Promise<string> => {
  const digest = await sha256(new TextEncoder().encode(codeVerifier))
  return base64UrlEncode(digest)
}

/**
 * Start a login attempt: generate a fresh verifier and state, persist them for
 * the login round trip (external browser on native, full-page redirect on web
 * — either may restart the app before the callback arrives), and return the
 * challenge and state to send with the authorize request. Overwrites any
 * previous attempt.
 */
export const beginLoginAttempt = async (): Promise<LoginAttemptStart> => {
  const verifier = generateCodeVerifier()
  // The state has the same entropy and encoding requirements as the verifier
  const state = generateCodeVerifier()
  const stored: StoredLoginAttempt = { verifier, state, createdAt: Date.now() }
  await StorageService.setItem(StorageKeys.EHealthPkceCodeVerifier, stored)
  return { codeChallenge: await computeCodeChallenge(verifier), state }
}

/**
 * Take the stored attempt for the callback. Single use: it is removed from
 * storage on read, and a stale one (older than the server's
 * authorization_request expiry) or an incomplete one is treated as absent.
 */
export const consumeLoginAttempt = async (): Promise<LoginAttempt | null> => {
  const raw = await StorageService.getItem(StorageKeys.EHealthPkceCodeVerifier)
  if (!raw) return null

  await StorageService.removeItem(StorageKeys.EHealthPkceCodeVerifier)

  try {
    const stored = JSON.parse(raw) as StoredLoginAttempt
    if (!stored?.verifier || !stored.state || Date.now() - stored.createdAt > ATTEMPT_TTL_MS) {
      return null
    }
    return { verifier: stored.verifier, state: stored.state }
  } catch {
    return null
  }
}
